import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleCOMPASS } from "@/components/compass/ModuleTitleCOMPASS";

export default function CompassSettings() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/compass/settings");
      setState(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put("/compass/settings", patch);
      toast.success("COMPASS settings updated.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  if (!state) return <p className="italic text-charcoal-muted">Loading…</p>;

  return (
    <div className="space-y-6" data-testid="compass-settings">
      <Link to="/compass" className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> COMPASS home
      </Link>
      <div>
        <ModuleTitleCOMPASS />
        <h1 className="mt-3 text-4xl font-serif text-forest-900">COMPASS Content</h1>
        <p className="mt-2 max-w-2xl text-charcoal-soft">
          Edit the landing tagline, the acronym definitions, the Fairness by Design blocks,
          the at-will disclaimer, and the retaliation attestation text. All changes are audit-logged.
        </p>
      </div>

      {/* Tagline */}
      <div className="patria-card p-5">
        <h3 className="text-xl font-serif text-forest-900 mb-2">Tagline</h3>
        <input
          className="patria-input w-full"
          value={state.tagline || ""}
          onChange={(e) => setState({ ...state, tagline: e.target.value })}
          data-testid="settings-tagline"
        />
      </div>

      {/* Acronym */}
      <div className="patria-card p-5">
        <h3 className="text-xl font-serif text-forest-900 mb-2">Acronym Definitions</h3>
        <ul className="space-y-3">
          {state.acronym.map((a, idx) => (
            <li key={a.key} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start" data-testid={`settings-acronym-${a.key}`}>
              <input className="patria-input sm:col-span-1" value={a.letter} onChange={(e) => setState({ ...state, acronym: state.acronym.map((x, i) => i === idx ? { ...x, letter: e.target.value } : x) })} />
              <input className="patria-input sm:col-span-3" value={a.label} onChange={(e) => setState({ ...state, acronym: state.acronym.map((x, i) => i === idx ? { ...x, label: e.target.value } : x) })} />
              <input className="patria-input sm:col-span-8" value={a.definition} onChange={(e) => setState({ ...state, acronym: state.acronym.map((x, i) => i === idx ? { ...x, definition: e.target.value } : x) })} />
            </li>
          ))}
        </ul>
        <button className="patria-btn-primary mt-3" onClick={() => save({ acronym: state.acronym, tagline: state.tagline })} disabled={saving} data-testid="settings-save-acronym">
          <Save className="h-4 w-4" strokeWidth={1.5} /> Save
        </button>
      </div>

      {/* Fairness blocks */}
      <div className="patria-card p-5">
        <h3 className="text-xl font-serif text-forest-900 mb-2">Fairness By Design</h3>
        <ul className="space-y-3">
          {state.fairness_blocks.map((b, idx) => (
            <li key={b.key} className="grid grid-cols-1 sm:grid-cols-6 gap-2" data-testid={`settings-fairness-${b.key}`}>
              <input className="patria-input sm:col-span-2" value={b.heading} onChange={(e) => setState({ ...state, fairness_blocks: state.fairness_blocks.map((x, i) => i === idx ? { ...x, heading: e.target.value } : x) })} />
              <textarea className="patria-input sm:col-span-4 min-h-[90px]" value={b.body} onChange={(e) => setState({ ...state, fairness_blocks: state.fairness_blocks.map((x, i) => i === idx ? { ...x, body: e.target.value } : x) })} />
            </li>
          ))}
        </ul>
        <button className="patria-btn-primary mt-3" onClick={() => save({ fairness_blocks: state.fairness_blocks })} disabled={saving} data-testid="settings-save-fairness">
          <Save className="h-4 w-4" strokeWidth={1.5} /> Save
        </button>
      </div>

      {/* At-will disclaimer + retaliation text */}
      <div className="patria-card p-5">
        <h3 className="text-xl font-serif text-forest-900 mb-2">At-Will Disclaimer</h3>
        <textarea
          className="patria-input w-full min-h-[130px]"
          value={state.at_will_disclaimer || ""}
          onChange={(e) => setState({ ...state, at_will_disclaimer: e.target.value })}
          data-testid="settings-at-will"
        />
        <h3 className="text-xl font-serif text-forest-900 mb-2 mt-4">Anti-Retaliation Attestation Text</h3>
        <textarea
          className="patria-input w-full min-h-[110px]"
          value={state.retaliation_attestation_text || ""}
          onChange={(e) => setState({ ...state, retaliation_attestation_text: e.target.value })}
          data-testid="settings-retaliation"
        />
        <button
          className="patria-btn-primary mt-3"
          onClick={() => save({
            at_will_disclaimer: state.at_will_disclaimer,
            retaliation_attestation_text: state.retaliation_attestation_text,
          })}
          disabled={saving}
          data-testid="settings-save-legal"
        >
          <Save className="h-4 w-4" strokeWidth={1.5} /> Save
        </button>
      </div>
    </div>
  );
}
